import React, { useState } from 'react';
import { 
  MagnifyingGlassIcon, 
  FilmIcon, 
  PlayIcon,
  ClockIcon,
  EyeIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import { searchContent } from '../services/api';

interface SearchMatch {
  word: string;
  startTime: number;
  endTime: number;
  confidence: number;
  context?: string;
  source?: 'keywords' | 'transcript';
}

interface SearchResult {
  contentId: string;
  contentTitle: string;
  contentType: string;
  thumbnailUrl?: string;
  durationSeconds?: number;
  matches: SearchMatch[];
  totalMatches: number;
}

interface SearchResponse {
  results: SearchResult[];
  query: string;
  totalResults: number;
  totalMatches: number;
  searchMethods?: {
    keywords: boolean;
    transcripts: boolean;
  };
  pagination: {
    offset: number;
    limit: number;
    hasMore: boolean;
  };
}

const ContentSearch: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchStats, setSearchStats] = useState<{
    totalResults: number;
    totalMatches: number;
    query: string;
    searchMethods?: {
      keywords: boolean;
      transcripts: boolean;
    };
  } | null>(null);

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const formatDuration = (seconds: number | undefined): string => {
    if (!seconds) return '—';
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    if (hours > 0) {
      return `${hours}:${(minutes % 60).toString().padStart(2, '0')}:${(seconds % 60).toString().padStart(2, '0')}`;
    }
    return `${minutes}:${(seconds % 60).toString().padStart(2, '0')}`;
  };

  const getContentTypeLabel = (type: string): string => {
    switch (type) {
      case 'movie':
        return 'Film';
      case 'series':
        return 'Dizi';
      case 'documentary':
        return 'Belgesel';
      case 'live':
        return 'Canlı';
      default:
        return type;
    }
  };

  const getContentTypeColor = (type: string): string => {
    switch (type) {
      case 'movie':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'series':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'documentary':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'live':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const getSourceLabel = (source: string | undefined): string => {
    switch (source) {
      case 'keywords':
        return 'Anahtar Kelime';
      case 'transcript':
        return 'Transkript';
      default:
        return 'Kelime';
    }
  };

  const getSourceColor = (source: string | undefined): string => {
    switch (source) {
      case 'keywords':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'transcript':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const highlightSearchQuery = (text: string, query: string): string => {
    if (!text || !query) return text;
    
    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    return text.replace(regex, '<mark class="bg-yellow-200 dark:bg-yellow-900 px-1 rounded">$1</mark>');
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setError('Lütfen arama yapmak için bir kelime girin');
      return;
    }

    setLoading(true);
    setError(null);
    setHasSearched(true);

    try {
      const response = await searchContent({
        query: searchQuery,
        limit: 50,
        offset: 0
      }) as { success: boolean; data: SearchResponse };

      console.log('🔍 DEBUG: Full API response:', response);
      
      if (response.success && response.data) {
        setSearchResults(response.data.results || []);
        setSearchStats({
          totalResults: response.data.totalResults || 0,
          totalMatches: response.data.totalMatches || 0,
          query: response.data.query || searchQuery,
          searchMethods: response.data.searchMethods
        });
      } else {
        throw new Error('API response format is invalid');
      }
    } catch (err: any) {
      console.error('Search error:', err);
      setError(err.message || 'Arama sırasında bir hata oluştu');
      setSearchResults([]);
      setSearchStats(null);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setError(null);
    setHasSearched(false);
    setSearchStats(null);
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">İçerik Arama</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Tüm içeriklerde kelime arayın ve hangi saniyelerinde geçtiğini görün
        </p>
      </div>

      {/* Search Box */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 transition-colors">
        <div className="flex gap-4">
          <div className="flex-1 relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Aranacak kelimeyi girin..."
              className="block w-full pl-10 pr-3 py-3 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-700 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg dark:text-white"
            />
          </div>
          <button
            onClick={handleSearch}
            disabled={loading || !searchQuery.trim()}
            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? 'Arıyor...' : 'Ara'}
          </button>
          {hasSearched && (
            <button
              onClick={clearSearch}
              className="inline-flex items-center px-4 py-3 border border-gray-300 dark:border-gray-600 text-base font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              Temizle
            </button>
          )}
        </div>

        {/* Search Stats */}
        {searchStats && !loading && (
          <div className="mt-4 space-y-2">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <span className="font-medium">{searchStats.totalResults}</span> içerikte 
              <span className="font-medium"> {searchStats.totalMatches}</span> eşleşme bulundu: 
              <span className="font-medium"> "{searchStats.query}"</span>
            </div>
            {searchStats.searchMethods && (
              <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                <span>Arama yapılan yerler:</span>
                {searchStats.searchMethods.keywords && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                    Anahtar Kelimeler
                  </span>
                )}
                {searchStats.searchMethods.transcripts && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                    Transkriptler
                  </span>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700 rounded-lg p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-700 dark:text-red-200">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 transition-colors">
          <div className="animate-pulse space-y-4">
            {Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <div className="flex items-start space-x-4">
                  <div className="w-16 h-12 bg-gray-200 dark:bg-gray-700 rounded"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Search Results */}
      {!loading && hasSearched && searchResults.length > 0 && (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 transition-colors">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            Arama Sonuçları
          </h3>
          <div className="space-y-4">
            {searchResults.map((result) => (
              <div key={result.contentId} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                <div className="flex items-start space-x-4">
                  {/* Thumbnail */}
                  <div className="flex-shrink-0">
                    {result.thumbnailUrl ? (
                      <img
                        src={result.thumbnailUrl}
                        alt={result.contentTitle}
                        className="w-16 h-12 object-cover rounded"
                      />
                    ) : (
                      <div className="w-16 h-12 bg-gray-200 dark:bg-gray-700 rounded flex items-center justify-center">
                        <FilmIcon className="h-6 w-6 text-gray-400" />
                      </div>
                    )}
                  </div>

                  {/* Content Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div>
                        <Link
                          to={`/content/${result.contentId}`}
                          className="text-lg font-medium text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                        >
                          {result.contentTitle}
                        </Link>
                        <div className="flex items-center mt-1 space-x-3">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getContentTypeColor(result.contentType)}`}>
                            {getContentTypeLabel(result.contentType)}
                          </span>
                          {result.durationSeconds && (
                            <span className="inline-flex items-center text-sm text-gray-500 dark:text-gray-400">
                              <ClockIcon className="h-4 w-4 mr-1" />
                              {formatDuration(result.durationSeconds)}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-4">
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {result.totalMatches} eşleşme
                        </span>
                        <Link
                          to={`/content/${result.contentId}`}
                          className="inline-flex items-center text-sm text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300"
                        >
                          <EyeIcon className="h-4 w-4 mr-1" />
                          Detay
                        </Link>
                      </div>
                    </div>

                    {/* Matches */}
                    <div className="mt-3">
                      <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                        Eşleşmeler:
                      </div>
                      <div className="space-y-2">
                        {result.matches.slice(0, 6).map((match, index) => (
                          <div key={index} className="flex items-start space-x-3">
                            <Link
                              to={`/content/${result.contentId}?t=${Math.floor(match.startTime)}`}
                              className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors flex-shrink-0"
                            >
                              <PlayIcon className="h-3 w-3 mr-1" />
                              {formatTime(match.startTime)}
                            </Link>
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getSourceColor(match.source)} flex-shrink-0`}>
                              {getSourceLabel(match.source)}
                            </span>
                            {match.context && (
                              <div className="flex-1 min-w-0">
                                <p 
                                  className="text-sm text-gray-600 dark:text-gray-400 truncate"
                                  dangerouslySetInnerHTML={{ 
                                    __html: highlightSearchQuery(match.context, searchQuery) 
                                  }}
                                />
                              </div>
                            )}
                            {!match.context && (
                              <span className="text-sm font-medium text-gray-900 dark:text-white">
                                "{match.word}"
                              </span>
                            )}
                          </div>
                        ))}
                        {result.matches.length > 6 && (
                          <div className="text-center">
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
                              +{result.matches.length - 6} eşleşme daha
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* No Results State */}
      {!loading && hasSearched && searchResults.length === 0 && !error && (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 transition-colors">
          <div className="text-center py-8">
            <MagnifyingGlassIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">Sonuç bulunamadı</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              "{searchQuery}" için herhangi bir eşleşme bulunamadı.
            </p>
            <div className="mt-6">
              <button
                onClick={clearSearch}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Yeni arama yap
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!hasSearched && (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 transition-colors">
          <div className="text-center py-12">
            <MagnifyingGlassIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">İçerik Arama</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Tüm içeriklerde arama yapmak için yukarıdaki arama kutusunu kullanın.
            </p>
            <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">
              Sistem, içeriklerin hem anahtar kelimelerinde hem de transkript verilerinde arama yapar.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContentSearch; 